import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { History, Megaphone } from "lucide-react";
import type {
  CallOutcome,
  CampaignTicketContext,
} from "@shared-core/data/api-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import {
  CALL_OUTCOMES,
  CALL_OUTCOME_LABELS,
  OTHER_OUTCOME_NOTE_MAX_LENGTH,
  campaignTicketSendLines,
  type CampaignTicketSendLine,
} from "@shared-core/domain/direct-mail-campaigns";
import { formatShortDateOrDash } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { CampaignEmailPreviewTab } from "../marketing/CampaignEmailPreviewTab";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { cn } from "../../primitives/utils";

const NOTE_REQUIRED = "Say what happened in a short note.";

/**
 * The campaign side of a campaign ticket (one ticket per contact per
 * campaign): every send of the campaign listed on it, and the Outcome
 * picker, where one click records what happened on the call. The outcome
 * lands on the campaign's results and the contact's trail. Clicking another
 * outcome replaces it; "Clear" undoes one picked by mistake. "Other" asks
 * for a short note before it is recorded. An email send offers "Preview
 * email", which shows the email as sent without leaving the ticket.
 */
export function CampaignTicketSection({
  context,
  onRecord,
  onClear,
}: {
  context: CampaignTicketContext;
  onRecord: (outcome: CallOutcome, note?: string) => Promise<void>;
  onClear: () => Promise<void>;
}) {
  const [saving, setSaving] = useState<CallOutcome | "clear" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isNoteOpen, setIsNoteOpen] = useState(false);
  const [note, setNote] = useState("");
  const [noteError, setNoteError] = useState<string | null>(null);
  const [previewLine, setPreviewLine] = useState<CampaignTicketSendLine | null>(
    null,
  );
  const sendLines = campaignTicketSendLines(
    context.trail,
    context.campaign_name,
  );

  async function save(
    action: () => Promise<void>,
    what: CallOutcome | "clear",
  ) {
    setSaving(what);
    setError(null);
    try {
      await action();
      return true;
    } catch (err) {
      const noteFieldError = extractFieldErrors(err).find(
        (e) => e.field === "note",
      );
      if (noteFieldError) setNoteError(noteFieldError.message);
      else
        setError(
          what === "clear"
            ? "Couldn't clear the outcome. Please try again."
            : "Couldn't save the outcome. Please try again.",
        );
      return false;
    } finally {
      setSaving(null);
    }
  }

  async function choose(outcome: CallOutcome) {
    if (outcome === "other") {
      setNote(context.outcome === "other" ? (context.outcome_note ?? "") : "");
      setNoteError(null);
      setIsNoteOpen(true);
      return;
    }
    setIsNoteOpen(false);
    if (outcome === context.outcome) return;
    await save(() => onRecord(outcome), outcome);
  }

  async function saveOther(event: FormEvent) {
    event.preventDefault();
    const trimmed = note.trim();
    if (!trimmed) {
      setNoteError(NOTE_REQUIRED);
      return;
    }
    setNoteError(null);
    if (await save(() => onRecord("other", trimmed), "other"))
      setIsNoteOpen(false);
  }

  async function clear() {
    setIsNoteOpen(false);
    await save(onClear, "clear");
  }

  return (
    <section
      aria-labelledby="campaign-ticket-heading"
      className="rounded-lg border border-border bg-surface p-4 space-y-4"
    >
      <h3
        id="campaign-ticket-heading"
        className="text-sm font-medium flex items-center gap-2"
      >
        <Megaphone className="size-4 text-pink-700 dark:text-pink-300" />
        <Link
          to={paths.marketingCampaignWorkspace({
            campaignId: context.campaign_id,
          })}
          className="text-primary hover:underline"
        >
          {context.campaign_name}
        </Link>
        {context.tier_name && (
          <span className="text-muted-foreground font-normal">
            · {context.tier_name}
          </span>
        )}
      </h3>

      <div className="space-y-1.5">
        <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          This campaign
        </h4>
        {sendLines.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing from this campaign has reached this contact yet.
          </p>
        ) : (
          <ul className="space-y-1 text-sm text-foreground">
            {sendLines.map((line) => (
              <li key={line.stepId}>
                <span>{line.text}</span>
                {line.isEmail && (
                  <>
                    {" · "}
                    <button
                      type="button"
                      onClick={() => setPreviewLine(line)}
                      aria-label={`Preview email: ${line.text}`}
                      className="font-medium text-primary hover:underline"
                    >
                      Preview email
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h4
            id="campaign-ticket-outcome-heading"
            className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
          >
            Outcome
          </h4>
          {context.outcome && (
            <button
              type="button"
              onClick={clear}
              disabled={saving !== null}
              aria-label={`Clear the outcome ${CALL_OUTCOME_LABELS[context.outcome]}`}
              className="text-xs font-medium text-primary hover:underline disabled:opacity-60"
            >
              {saving === "clear" ? "Clearing…" : "Clear"}
            </button>
          )}
        </div>
        <div
          role="radiogroup"
          aria-labelledby="campaign-ticket-outcome-heading"
          className="grid gap-2 sm:grid-cols-2"
        >
          {CALL_OUTCOMES.map((outcome) => {
            const isSelected = context.outcome === outcome;
            return (
              <button
                key={outcome}
                type="button"
                role="radio"
                aria-checked={isSelected}
                aria-label={CALL_OUTCOME_LABELS[outcome]}
                disabled={saving !== null}
                onClick={() => choose(outcome)}
                className={cn(
                  "rounded-md border px-3 py-2 text-left text-sm transition-colors disabled:opacity-60",
                  isSelected
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background text-foreground hover:bg-surface-hover",
                )}
              >
                {saving === outcome ? "Saving…" : CALL_OUTCOME_LABELS[outcome]}
              </button>
            );
          })}
        </div>
        {context.outcome === "other" && context.outcome_note && !isNoteOpen && (
          <p className="text-sm text-foreground">
            Other – {context.outcome_note}
          </p>
        )}
        {isNoteOpen && (
          <form onSubmit={saveOther} className="space-y-2" noValidate>
            <label
              htmlFor="campaign-ticket-other-note"
              className="block text-sm font-medium"
            >
              What happened?
            </label>
            <input
              id="campaign-ticket-other-note"
              type="text"
              value={note}
              maxLength={OTHER_OUTCOME_NOTE_MAX_LENGTH}
              onChange={(e) => setNote(e.target.value)}
              aria-invalid={noteError !== null}
              aria-describedby={
                noteError
                  ? "campaign-ticket-other-note-error"
                  : "campaign-ticket-other-note-hint"
              }
              autoFocus
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary aria-[invalid=true]:border-red-600"
            />
            {noteError ? (
              <p
                id="campaign-ticket-other-note-error"
                className="text-sm text-red-600"
              >
                {noteError}
              </p>
            ) : (
              <p
                id="campaign-ticket-other-note-hint"
                className="text-xs text-muted-foreground"
              >
                A short note, up to {OTHER_OUTCOME_NOTE_MAX_LENGTH} characters.
              </p>
            )}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saving !== null}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-60"
              >
                {saving === "other" ? "Saving…" : "Save outcome"}
              </button>
              <button
                type="button"
                onClick={() => setIsNoteOpen(false)}
                disabled={saving !== null}
                className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
        {!context.outcome && !error && !isNoteOpen && (
          <p className="text-xs text-muted-foreground">
            Not recorded yet. Pick what happened on the call.
          </p>
        )}
        {error && (
          <p className="text-sm text-red-700 dark:text-red-300" role="alert">
            {error}
          </p>
        )}
      </div>

      <Dialog
        open={previewLine !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPreviewLine(null);
        }}
      >
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email preview</DialogTitle>
            <DialogDescription>
              {context.campaign_name}
              {previewLine && ` · ${previewLine.text}`}
            </DialogDescription>
          </DialogHeader>
          {previewLine && (
            <CampaignEmailPreviewTab
              campaignId={context.campaign_id}
              stepId={previewLine.stepId}
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

/**
 * Shown at the top of a campaign ticket raised after the same contact's
 * earlier ticket for the campaign was closed, so the rep can decide whether
 * to call again.
 */
export function EarlierCampaignTicketBanner({
  context,
}: {
  context: CampaignTicketContext;
}) {
  const earlier = context.earlier_closed_ticket;
  if (!earlier) return null;
  return (
    <div
      role="note"
      className="flex flex-wrap items-start gap-x-3 gap-y-1 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 px-4 py-3 text-sm text-amber-950 dark:text-amber-100"
    >
      <History className="size-4 mt-0.5 shrink-0" />
      <p className="flex-1 min-w-0">
        You spoke to this contact on {formatShortDateOrDash(earlier.spoke_on)}{" "}
        about {context.campaign_name} — ticket closed:{" "}
        {earlier.outcome
          ? CALL_OUTCOME_LABELS[earlier.outcome]
          : "no outcome recorded"}
        .
      </p>
      <Link
        to={paths.ticketDetail({ id: earlier.ticket_id })}
        className="font-medium underline hover:no-underline"
      >
        Open the earlier ticket
      </Link>
    </div>
  );
}
